import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { ContentBlockType } from '@onesource/schemas';
import { useGetLesson, useGetLessons, useGetSpace } from '@onesource/services';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import { LessonBreadcrumbs } from '#/features/community-hub/components/lesson-breadcrumbs';
import { VideoBlockPlayer } from '#/features/community-hub/components/lesson-detail-view/video-block-player';

/**
 * Video Content Route
 * Path: /community-hub/spaces/:id/modules/:moduleId/lessons/:lessonId/video/:contentId
 * Shows a single video content block
 */
export const Route = createFileRoute(
  '/_protected/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/video/$contentId',
)({
  component: VideoContentRoute,
});

function VideoContentRoute() {
  const { id: spaceId, moduleId, lessonId, contentId } = Route.useParams();

  // Data fetching hooks
  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useGetLesson(lessonId);
  const { data: module } = useGetSpace(moduleId);
  const { data: lessons } = useGetLessons(moduleId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId);

  // Find the video content block
  const videoBlock = lesson?.contentBlocks?.find(
    block => block.id === contentId && block.blockType === ContentBlockType.VIMEO,
  );

  const handleClose = useCallback(() => {
    window.history.back();
  }, []);

  // Loading state
  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load content: {lessonError.message}</Alert>
      </Box>
    );
  }

  // Video not found
  if (!videoBlock) {
    return (
      <Box p={3}>
        <Alert severity="error">Video not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <LessonBreadcrumbs
        spaceId={spaceId}
        moduleId={moduleId}
        lessonId={lessonId}
        module={module}
        lesson={lesson}
        parentLesson={findParentLesson}
        currentLabel="Video"
      />

      <Box sx={{ width: { xs: '100%', md: 'calc(100% - 190px)' } }}>
        <VideoBlockPlayer block={videoBlock} lessonId={lessonId} onClose={handleClose} />
      </Box>
    </Box>
  );
}
